/*
    AboutPage.jsx
    当団体について - About Page
*/

import React from "react";
import { useCms } from "../cms/CmsProvider";
import { motion } from "framer-motion";
import aboutImage from '../assets/img/aboutus_stock.jpg';

export default function AboutPage() {
    const { t, text, image, list, paragraphs, items, isJa } = useCms("about");
    const story = paragraphs("story", [1, 2, 3].map((n) => `aboutPage.story.paragraph${n}`));
    const activities = list("activities", [1, 2, 3, 4].map((n) => `aboutPage.activities.item${n}`));
    const vision = paragraphs("vision", [1, 2].map((n) => `aboutPage.vision.paragraph${n}`));
    const defaultHistory = t("aboutPage.representative.history", { returnObjects: true });
    const history = list("repHistory", []).length > 0 ? list("repHistory", []) : (Array.isArray(defaultHistory) ? defaultHistory : []);
    const defaultMembers = t("aboutPage.representative.boardMembers.members", { returnObjects: true });
    const members = items("boardMembers", Array.isArray(defaultMembers) ? defaultMembers : []);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2, duration: 0.6 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    return (
        <div className="about-page">
            {/* Header */}
            <section className="about-page-header">
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    {text("title", "aboutPage.title")}
                </motion.h1>
            </section>

            {/* Story Section with Image on Right */}
            <motion.section
                className="about-story-section"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, threshold: 0.2 }}
            >
                <div className="about-content-with-image">
                    <div className="about-text-content">
                        {story.map((p, i) => (
                            <motion.div className="about-card" variants={itemVariants} key={i}>
                                <p style={{ whiteSpace: "pre-line" }}>{p}</p>
                            </motion.div>
                        ))}
                    </div>
                    <motion.div className="about-side-image" variants={itemVariants}>
                        <img src={image("image", aboutImage)} alt="子どもたちの活動" />
                    </motion.div>
                </div>
            </motion.section>

            {/* Activities Section */}
            <motion.section
                className="about-activities-section"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, threshold: 0.2 }}
            >
                <motion.h2 variants={itemVariants}>{text("activitiesTitle", "aboutPage.activities.title")}</motion.h2>
                <ul className="activities-list-simple">
                    {activities.map((item, i) => (
                        <motion.li variants={itemVariants} key={i}>{item}</motion.li>
                    ))}
                </ul>
            </motion.section>

            {/* Vision Section */}
            <motion.section
                className="about-vision-section"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, threshold: 0.2 }}
            >
                <motion.h2 variants={itemVariants}>{text("visionTitle", "aboutPage.vision.title")}</motion.h2>
                <motion.div className="vision-content" variants={itemVariants}>
                    {vision.map((p, i) => (
                        <p key={i} style={{ whiteSpace: "pre-line" }}>{p}</p>
                    ))}
                </motion.div>
            </motion.section>

            {/* Representative Section */}
            <motion.section
                className="about-representative-section"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, threshold: 0.2 }}
            >
                <motion.h2 variants={itemVariants}>{text("repTitle", "aboutPage.representative.title")}</motion.h2>
                <motion.div className="representative-content" variants={itemVariants}>
                    <div className="representative-header">
                        <h3>{text("repName", "aboutPage.representative.name")}</h3>
                        <span className="representative-qualifications">{text("repQualifications", "aboutPage.representative.qualifications")}</span>
                    </div>
                    <ul className="representative-history">
                        {history.map((item, index) => (
                            <li key={index}>{item}</li>
                        ))}
                    </ul>
                </motion.div>

                {/* Board Members */}
                <motion.div className="board-members-content" variants={itemVariants}>
                    <h3>{text("boardTitle", "aboutPage.representative.boardMembers.title")}</h3>
                    <div className="board-members-list">
                        {members.map((member, index) => (
                            <div className="board-member-item" key={index}>
                                <span className="board-member-role">{member.role}</span>
                                <span className="board-member-name">{member.name}</span>
                                {member.profession && <span className="board-member-profession">{isJa ? `（${member.profession}）` : `(${member.profession})`}</span>}
                            </div>
                        ))}
                    </div>
                </motion.div>
            </motion.section>
        </div>
    );
}
